import { useEffect, useState } from 'react';

import { useStore } from '@/providers/app-store-provider';

export function usePageCache({
  pageKey = '',
  initialData = {},
}: {
  pageKey: string;
  initialData: any;
}) {
  const [isFromCache, setIsFromCache] = useState(false);
  const [pageState, setPageState] = useState(initialData);

  // 从全局 zustand store 获取缓存操作方法
  const getPageCache = useStore((state) => state.getPageCache);
  const setPageCacheToStore = useStore((state) => state.setPageCache);
  const updatePageCacheToStore = useStore((state) => state.updatePageCache);

  // 初始化时从 zustand store 读取数据
  useEffect(() => {
    // 从 zustand store 获取缓存数据
    const cachedData = getPageCache(pageKey);

    if (cachedData) {
      setIsFromCache(true);
      setPageState(cachedData);

      // 恢复滚动位置
      if (cachedData.scrollY) {
        setTimeout(() => {
          window.scrollTo({ top: cachedData.scrollY, behavior: 'instant' });
        }, 100);
      }
    } else {
      // 使用初始数据并保存到 store
      setPageCacheToStore(pageKey, initialData);
      setPageState(initialData);
      setIsFromCache(false);
    }
  }, []); // 只执行一次

  // 记录滚动位置
  useEffect(() => {
    const saveScrollPosition = () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const currentData = getPageCache(pageKey) || {};

      // 只有滚动位置发生变化时才更新
      if (currentData.scrollY !== currentScrollY) {
        updatePageCacheToStore(pageKey, { scrollY: currentScrollY });
        setPageState((prev: any) => ({ ...prev, scrollY: currentScrollY }));
      }
    };

    // 监听页面离开事件
    window.addEventListener('beforeunload', saveScrollPosition);

    // 组件卸载时也保存一次
    return () => {
      saveScrollPosition();
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [pageKey, getPageCache, updatePageCacheToStore]);

  // 更新页面状态的函数
  const updatePageState = (newState: any) => {
    // 同时更新到 zustand store
    setPageState((prev: any) => ({ ...prev, ...newState }));
    updatePageCacheToStore(pageKey, newState);
  };

  // 返回状态和更新函数
  return {
    pageState,
    updatePageState,
    isFromCache,
  };
}
